import axios from "axios";
import React, { useState, useEffect } from "react";
import Pattern_concentration_reco from "./_90_pattern_concentration_reco.js";
import Pattern_low_price_reco from "./_91_pattern_low_price_reco.js";
import TopHeader from "./nav_top.js";
import Pattern_symbol_attachment from "./_92_pattern_symbol_attachment.js";
import Pattern_scalping_reco from "./_93_pattern_scalping_reco.js";
import Pattern_extend_hours from "./_94_pattern_extend_hours.js";
import Pattern_near_open from "./_95_pattern_near_open.js";
import Multi_day_trade_pattern from "./_96_multi_day_trade_pattern.js";
import Pattern_over_trading from "./_97_pattern_over_trading.js";
import PatternCompare from "./_110_pattern_compare.js";
import AIPatternInsights from "./_999_ai_pattern_insights.js";
import LeftNav_patterns from "./LeftNav_patterns.js";
import { getLookbackDays } from "./patterns/helpers.js";
import { useDateRange } from "./dateRangeContext.js";
import "./styles.css";

const MODULE_NAME = "pg_patterns";
const storedId = sessionStorage.getItem("user_id");
const userId = storedId && !isNaN(Number(storedId)) ? Number(storedId) : 1;
const activeDate = new Date().toISOString().slice(0, 10);
const screen = "pg_patterns";
const API_URL = process.env.REACT_APP_API_URL;

// Static component library
const componentLibrary = {
    "_90_pattern_concentration_reco": Pattern_concentration_reco,
    "_91_pattern_low_price_reco": Pattern_low_price_reco,
    "_92_pattern_symbol_attachment": Pattern_symbol_attachment,
    "_93_pattern_scalping_reco": Pattern_scalping_reco,
    "_94_pattern_extend_hours": Pattern_extend_hours,
    "_95_pattern_near_open": Pattern_near_open,
    "_96_multi_day_trade_pattern": Multi_day_trade_pattern,
    "_97_pattern_over_trading": Pattern_over_trading,  // Ensure this matches database
};

// Helper: Convert component_name to key
function componentNameToKey(componentName) {
    return componentName
        .replace(/^_\d+_/, "")
        .replace(/_reco$/, "")
        .toLowerCase();
}

const LoadingWheel = ({ message }) => (
    <div
        style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100vh",
            background: "#181A25",
            gap: 24,
        }}
    >
        <div
            style={{
                width: 60,
                height: 60,
                border: "4px solid #444",
                borderTop: "4px solid #e0b400",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
            }}
        />
        <p style={{ color: "#ccc", fontSize: 16, textAlign: "center" }}>
            {message}
        </p>
        <style>{`
            @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
            }
        `}</style>
    </div>
);

const pg_patterns = ({ setScreen, refreshKey }) => {
    const [selected, setSelected] = useState(null);
    const [navPinned, setNavPinned] = useState(true);
    const [navHovered, setNavHovered] = useState(false);
    const [loadingNav, setLoadingNav] = useState(true);
    const [loadingMessage, setLoadingMessage] = useState("Initializing patterns...");
    const [patternData, setPatternData] = useState({});
    const [allPatterns, setAllPatterns] = useState([]);
    const [enabledPatterns, setEnabledPatterns] = useState([]);
    const [patternComponents, setPatternComponents] = useState({});
    const [navGroups, setNavGroups] = useState([]);
    const [updatingPattern, setUpdatingPattern] = useState(null);
    const { windowDays } = useDateRange();

    // ✅ STEP 1: Fetch pattern settings and ALL pattern data via pattern_compare
    useEffect(() => {
        const fetchAllPatternData = async () => {
            try {
                setLoadingNav(true);
                setLoadingMessage("Loading patterns...");

                // Fetch pattern settings
                const settingsRes = await axios.get(`${API_URL}/api/user-pattern-settings`, {
                    withCredentials: true,
                });

                const allPats = settingsRes.data.patterns || [];
                const settings = settingsRes.data.settings || {};

                // ✅ FIXED: Default to enabled for new users without settings
                const patternsWithStatus = allPats.map(p => {
                    const userSetting = settings[p.pattern_id];
                    const isEnabled = userSetting !== undefined 
                        ? userSetting.enabled === 1 
                        : (p.enabled === 1 || p.enabled === undefined);
                    return { ...p, enabled: isEnabled };
                });
                setAllPatterns(patternsWithStatus);

                // Build enabled patterns list
                const enabledPats = patternsWithStatus.filter(p => p.enabled);
                const endpoints = enabledPats.map(p => ({
                    key: componentNameToKey(p.component_name),
                    label: p.name,
                    pattern_id: p.pattern_id,
                    component_name: p.component_name,
                }));

                // Build component map
                const components = {};
                endpoints.forEach(ep => {
                    const component = componentLibrary[ep.component_name];
                    if (component) {
                        components[ep.key] = component;
                    }
                });

                setEnabledPatterns(endpoints);
                setPatternComponents(components);

                // Build nav groups
                const allEndpoints = patternsWithStatus.map(p => ({
                    key: componentNameToKey(p.component_name),
                    label: p.name,
                    pattern_id: p.pattern_id,
                    component_name: p.component_name,
                    enabled: p.enabled,
                }));

                const pricePatterns = allEndpoints.filter(p =>
                    p.label.toLowerCase().includes("concentration") ||
                    p.label.toLowerCase().includes("low price") ||
                    p.label.toLowerCase().includes("low-price") ||
                    p.label.toLowerCase().includes("symbol attachment")
                );

                const timePatterns = allEndpoints.filter(p => {
                    const label = p.label.toLowerCase();
                    return (
                        label.includes("near open") ||
                        label.includes("first half hour") ||
                        label.includes("extended hours") ||
                        label.includes("hours")
                    );
                });

                const assignedKeys = new Set([
                    ...pricePatterns.map(p => p.key),
                    ...timePatterns.map(p => p.key)
                ]);

                const otherPatterns = allEndpoints.filter(p => !assignedKeys.has(p.key));

                const groups = [
                    ...(pricePatterns.length > 0 ? [{
                        label: "Price",
                        patterns: pricePatterns.map(p => ({
                            key: p.key,
                            label: p.label,
                            component_name: p.component_name,
                            pattern_id: p.pattern_id,
                            enabled: p.enabled,
                        })),
                    }] : []),
                    ...(timePatterns.length > 0 ? [{
                        label: "Time",
                        patterns: timePatterns.map(p => ({
                            key: p.key,
                            label: p.label,
                            component_name: p.component_name,
                            pattern_id: p.pattern_id,
                            enabled: p.enabled,
                        })),
                    }] : []),
                    ...(otherPatterns.length > 0 ? [{
                        label: "Other",
                        patterns: otherPatterns.map(p => ({
                            key: p.key,
                            label: p.label,
                            component_name: p.component_name,
                            pattern_id: p.pattern_id,
                            enabled: p.enabled,
                        })),
                    }] : []),
                ];

                setNavGroups(groups);

                // ✅ SINGLE CALL: Fetch ALL pattern data via pattern_compare
                const lookback = getLookbackDays(windowDays);
                const compareRes = await fetch(
                    `${API_URL}/api/pattern_compare?lookback_days=${lookback}`,
                    { credentials: "include" }
                );

                if (compareRes.ok) {
                    const compareData = await compareRes.json();
                    const patternsArray = compareData.patterns || [];

                    // Map pattern data by key for quick lookup
                    const dataByKey = {};
                    patternsArray.forEach(patData => {
                        const matchingEndpoint = endpoints.find(ep => ep.pattern_id === patData.pattern_id);
                        if (matchingEndpoint) {
                            dataByKey[matchingEndpoint.key] = patData;
                        }
                    });

                    setPatternData(dataByKey);
                }

                // Select first enabled pattern as default
                if (endpoints.length > 0) {
                    setSelected(endpoints[0].key);
                }

                setLoadingNav(false);
            } catch (err) {
                console.error("Error fetching pattern data:", err);
                setLoadingNav(false);
            }
        };

        fetchAllPatternData();
    }, [windowDays]);

    // ✅ STEP 2: Handle pattern toggle (enable/disable)
    const handlePatternToggle = async (patternId, newEnabled) => {
        setUpdatingPattern(patternId);
        try {
            await axios.post(
                `${API_URL}/api/user-pattern-settings/toggle`,
                { pattern_id: patternId, enabled: newEnabled ? 1 : 0 },
                { withCredentials: true }
            );

            // Update all patterns state
            const updated = allPatterns.map(p =>
                p.pattern_id === patternId ? { ...p, enabled: newEnabled } : p
            );
            setAllPatterns(updated);

            // Rebuild enabled patterns list
            const enabledPats = updated.filter(p => p.enabled);
            const endpoints = enabledPats.map(p => ({
                key: componentNameToKey(p.component_name),
                label: p.name,
                pattern_id: p.pattern_id,
                component_name: p.component_name,
            }));

            setEnabledPatterns(endpoints);

            // Update nav groups with new enabled status
            const updatedNavGroups = navGroups.map(group => ({
                ...group,
                patterns: group.patterns.map(pat => ({
                    ...pat,
                    enabled: updated.find(p => p.pattern_id === pat.pattern_id)?.enabled ?? pat.enabled,
                }))
            }));
            setNavGroups(updatedNavGroups);

            // If disabled pattern was selected, switch to first enabled pattern
            const currentPattern = endpoints.find(ep => ep.key === selected);
            if (!currentPattern && endpoints.length > 0) {
                setSelected(endpoints[0].key);
            }

            window.dispatchEvent(new Event("patternEventUpdated"));
        } catch (err) {
            console.error(`Error updating pattern ${patternId}:`, err);
        } finally {
            setUpdatingPattern(null);
        }
    };

    if (loadingNav) {
        return (
            <>
                <TopHeader userId={userId} screen={screen} refreshKey={refreshKey} onNavigate={setScreen} hidden={true} />
                <LoadingWheel message={loadingMessage} />
            </>
        );
    }

    const SelectedComponent = patternComponents[selected];
    const selectedData = patternData[selected];
    const navExpanded = navPinned || navHovered;

    return (
        <>
            <TopHeader userId={userId} screen={screen} refreshKey={refreshKey} onNavigate={setScreen} />
            <div className="pg-patterns-container">
                <LeftNav_patterns
                    navExpanded={navExpanded}
                    navPinned={navPinned}
                    setNavPinned={setNavPinned}
                    setNavHovered={setNavHovered}
                    navGroups={navGroups}
                    selected={selected}
                    setSelected={setSelected}
                    loadingSelectedPattern={false}
                    updatingPattern={updatingPattern}
                    handlePatternToggle={handlePatternToggle}
                />

                <main style={{ flex: 1, padding: "32px 24px", background: "#181A25", minHeight: "100vh", overflow: "auto" }}>
                    <section style={{ width: "100%", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginBottom: 50 }}>
                        <div style={{ background: "rgba(25, 118, 210, 0.08)", borderRadius: 12, padding: 24, border: "1px solid rgba(25, 118, 210, 0.15)", backdropFilter: "blur(8px)", height: 255 }}>
                            <h2 style={{ color: "#1976d2", margin: "0 0 16px 0", fontSize: "1.2em", fontWeight: 600 }}>📊 Insights into your trading behavior</h2>
                            <PatternCompare />
                        </div>
                        <div>
                            <h2 style={{ color: "#00FFCC", margin: "0 0 16px 0", fontSize: "1.2em", fontWeight: 600 }}>🤖 AI Analyst</h2>
                            <AIPatternInsights />
                        </div>
                    </section>

                    <div style={{ width: "100%" }} data-analytics={MODULE_NAME}>
                        {SelectedComponent && selectedData && !selectedData.error ? (
                            <SelectedComponent userId={userId} activeDate={activeDate} screen={screen} preloadedData={selectedData} onNavigate={setScreen} />
                        ) : (
                            <div style={{ color: "#ccc", padding: "24px", textAlign: "center" }}>
                                {selectedData?.error ? `Error: ${selectedData.error}` : "No enabled patterns to display."}
                            </div>
                        )}
                    </div>

                    <style>{`
                        .pg-patterns-container { display: flex; min-height: 100vh; background: #181A25; flex-direction: row; }
                        @media (max-width: 768px) {
                            .pg-patterns-container { flex-direction: column; }
                            main { order: 1; }
                        }
                        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                    `}</style>
                </main>
            </div>
        </>
    );
};

export default pg_patterns;